import React, { useState } from "react";
import { Box, Button, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import { theme } from "../../../../styles/theme";
import EmailSubmit from "../../../../pages/tosspayments/EmailSubmit";
import { useMutation } from "@tanstack/react-query";
import { paymentApi } from "../../../../api/paymentApi";
import { useSearchParams } from "react-router-dom";
import CustomTypography from "../../../common/CustomTypography";
import { trackCapiStandardEvent } from "../../../../analytics/metaCapi";
import distancingLogo from "../../../../static/image/common/distancing_logo.png";

export default function CompletedPageV5() {
  const [searchParams] = useSearchParams();

  const orderId = searchParams.get("orderId");
  const paymentKey = searchParams.get("paymentKey");

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [text, setText] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);

  const isButtonDisabled = loading || text.trim() === "" || !isEmailValid;
  const [isEmailSubmitted, setEmailSubmitted] = useState(false);

  const { mutate: submitEmail } = useMutation({
    mutationFn: (vars: {
      orderId: string;
      paymentKey: string;
      email: string;
    }) =>
      paymentApi.submitEmailTossPaymentsPromotion({
        orderId: vars.orderId,
        paymentKey: vars.paymentKey,
        email: vars.email,
        version: "v5",
      }),
    onSuccess: () => {
      setEmailSubmitted(true);
      setLoading(false);

      // 이후 이벤트 전송
      trackCapiStandardEvent(
        "Purchase",
        {
          value: 395000,
          currency: "KRW",
          email: text,
        },
        {
          eventId: orderId ?? undefined,
        },
        "v5"
      );
    },
  });

  const handleEmailSubmit = (email: string) => {
    if (!isEmailValid) return;
    if (!orderId || !paymentKey) return;

    setLoading(true);

    submitEmail({ orderId, paymentKey, email });
  };

  return (
    <DefaultPage
      content={
        <Stack alignItems="center" spacing={4}>
          {!isEmailSubmitted ? (
            <Stack alignItems={"center"} spacing={4}>
              <img
                src={distancingLogo}
                alt="distancing logo"
                style={{ width: "138px", height: "auto" }}
              />
              <Stack alignItems={"center"} spacing={0.1}>
                <CustomTypography
                  variant={"Subtitle"}
                  sx={{
                    color: theme.vars.palette.Content.Strong,
                    fontWeight: 500,
                  }}
                >
                  {"프로그램 안내를 받을"}
                </CustomTypography>

                <Stack direction={"row"} spacing={"0.1px"}>
                  <CustomTypography
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.HighlightWithAlert,
                      fontWeight: 500,
                      px: 0,
                    }}
                  >
                    {"(필수)"}
                  </CustomTypography>
                  <CustomTypography
                    variant={"Subtitle"}
                    sx={{
                      color: theme.vars.palette.Content.Strong,
                      fontWeight: 500,
                    }}
                  >
                    {"이메일 주소를 입력해 주세요."}
                  </CustomTypography>
                </Stack>
              </Stack>
            </Stack>
          ) : (
            <Stack alignItems={"center"} spacing={4}>
              <img
                src={distancingLogo}
                alt="distancing logo"
                style={{ width: "138px", height: "auto" }}
              />
              <CustomTypography
                variant={"Subtitle"}
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  textAlign: "center",
                  fontWeight: 500,
                }}
              >
                {
                  "감사합니다.\n메일 발송까지 최대 1 영업일이\n소요될 수 있으니 조금만 기다려주세요."
                }
              </CustomTypography>
            </Stack>
          )}

          {!isEmailSubmitted && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "640px",
                  height: "54px",
                  backgroundColor: "transparent",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: `inset 0 0 0 1.333px ${theme.vars.palette.Border.Weak}`,
                }}
              >
                <EmailSubmit
                  initialValue={text}
                  handleChange={(newValue: string) => setText(newValue)}
                  onValidityChange={(valid: boolean) => setIsEmailValid(valid)}
                  isFocused={isFocused}
                  setIsFocused={setIsFocused}
                  placeholder={"이메일 주소를 입력해주세요"}
                />
              </Box>
            </Stack>
          )}

          {!isEmailSubmitted && (
            <Stack spacing={1.5} alignItems={"center"} sx={{ width: "100%" }}>
              <Button
                onClick={() => handleEmailSubmit(text)}
                disabled={isButtonDisabled}
                variant={"Solid"}
                color={"primary"}
                size={"lg"}
                sx={{
                  width: "100%",
                  maxWidth: "345px",
                  height: "54px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "16px",
                }}
              >
                {"제출하기"}
              </Button>
            </Stack>
          )}
        </Stack>
      }
    />
  );
}
